/**
 * UtilitiesTab Component
 * 
 * Quick utilities for chemistry calculations:
 * - Molecular weight calculator
 * - Equation balancer
 */

import React, { useState } from 'react';
import { Calculator, Scale } from 'lucide-react';
import { loadRDKit } from './rdkitLoader';
import { balanceEquation, BalancedEquation } from './equationBalancer';

export const UtilitiesTab: React.FC = () => {
  const [activeUtility, setActiveUtility] = useState<'mw' | 'balance'>('mw');
  const [mwInput, setMwInput] = useState('');
  const [mwResult, setMwResult] = useState<{ value: number; formula?: string } | null>(null);
  const [mwLoading, setMwLoading] = useState(false);
  const [mwError, setMwError] = useState<string | null>(null);

  const [equationInput, setEquationInput] = useState('');
  const [equationResult, setEquationResult] = useState<BalancedEquation | null>(null);
  const [equationError, setEquationError] = useState<string | null>(null);
  const [equationLoading, setEquationLoading] = useState(false);

  /**
   * Calculate molecular weight from SMILES or formula
   */
  const handleCalculateMW = async () => {
    if (!mwInput.trim()) {
      setMwError('Please enter a SMILES string or molecular formula');
      return;
    }

    setMwLoading(true);
    setMwError(null);
    setMwResult(null);

    try {
      const RDKit = await loadRDKit();
      
      // Try parsing as SMILES first
      let mol;
      try {
        mol = RDKit.get_mol(mwInput.trim());
      } catch (e) {
        throw new Error('Invalid SMILES string. Please enter a valid SMILES notation.');
      }

      if (!mol) {
        throw new Error('Failed to parse structure');
      }

      // Get descriptors
      const descriptorsJson = mol.get_descriptors();
      const descriptors = JSON.parse(descriptorsJson);

      const mw = descriptors.exactmw || descriptors.amw || null;
      const formula = descriptors.formula || descriptors.MolFormula || null;

      if (!mw) {
        throw new Error('Could not calculate molecular weight');
      }

      setMwResult({
        value: mw,
        formula: formula || undefined,
      });

      mol.delete();
    } catch (err: any) {
      setMwError(err.message || 'Failed to calculate molecular weight');
    } finally {
      setMwLoading(false);
    }
  };

  /**
   * Balance chemical equation using RDKit
   */
  const handleBalanceEquation = async () => {
    if (!equationInput.trim()) {
      setEquationError('Please enter an equation (e.g., H2 + O2 -> H2O)');
      return;
    }

    setEquationLoading(true);
    setEquationError(null);
    setEquationResult(null);

    try {
      const result = await balanceEquation(equationInput.trim());
      
      if (result.error) {
        setEquationError(result.error);
      } else {
        setEquationResult(result);
      }
    } catch (err: any) {
      setEquationError(err.message || 'Failed to balance equation');
    } finally {
      setEquationLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Quick Utilities</h3>
      </div>

      {/* Utility Selector */}
      <div style={styles.utilityTabs}>
        <button
          style={{
            ...styles.utilityTab,
            ...(activeUtility === 'mw' ? styles.utilityTabActive : {}),
          }}
          onClick={() => setActiveUtility('mw')}
        >
          <Calculator size={16} />
          Molecular Weight
        </button>
        <button
          style={{
            ...styles.utilityTab,
            ...(activeUtility === 'balance' ? styles.utilityTabActive : {}),
          }}
          onClick={() => setActiveUtility('balance')}
        >
          <Scale size={16} />
          Balance Equation
        </button>
      </div>

      {/* Molecular Weight Calculator */}
      {activeUtility === 'mw' && (
        <div style={styles.utilityContent}>
          <div style={styles.section}>
            <label style={styles.label}>
              Enter SMILES string or molecular formula:
            </label>
            <div style={styles.inputGroup}>
              <input
                type="text"
                style={styles.input}
                value={mwInput}
                onChange={(e) => setMwInput(e.target.value)}
                placeholder="e.g., CCO (ethanol) or C2H6O"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCalculateMW();
                  }
                }}
              />
              <button
                style={styles.calculateButton}
                onClick={handleCalculateMW}
                disabled={mwLoading}
              >
                {mwLoading ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
            {mwError && (
              <div style={styles.errorBox}>
                {mwError}
              </div>
            )}
            {mwResult && (
              <div style={styles.resultBox}>
                <div style={styles.resultTitle}>Result:</div>
                <div style={styles.resultValue}>
                  <strong>Molecular Weight:</strong> {mwResult.value.toFixed(4)} g/mol
                </div>
                {mwResult.formula && (
                  <div style={styles.resultValue}>
                    <strong>Formula:</strong> {mwResult.formula}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Equation Balancer */}
      {activeUtility === 'balance' && (
        <div style={styles.utilityContent}>
          <div style={styles.section}>
            <label style={styles.label}>
              Enter chemical equation:
            </label>
            <div style={styles.inputGroup}>
              <input
                type="text"
                style={styles.input}
                value={equationInput}
                onChange={(e) => setEquationInput(e.target.value)}
                placeholder="e.g., H2 + O2 -> H2O"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleBalanceEquation();
                  }
                }}
              />
              <button
                style={styles.calculateButton}
                onClick={handleBalanceEquation}
                disabled={equationLoading}
              >
                {equationLoading ? 'Balancing...' : 'Balance'}
              </button>
            </div>
            {equationError && (
              <div style={styles.errorBox}>
                {equationError}
              </div>
            )}
            {equationResult && !equationResult.error && (
              <div style={styles.resultBox}>
                <div style={styles.resultTitle}>Balanced Equation:</div>
                <div style={styles.equationResult}>
                  <div style={styles.balancedEquation}>{equationResult.balanced}</div>
                  <div style={styles.coefficientsTable}>
                    <div style={styles.coefficientsSection}>
                      <strong>Reactants:</strong>
                      {equationResult.reactants.map((r, i) => (
                        <div key={i} style={styles.coefficientRow}>
                          {r.coefficient} {r.formula}
                        </div>
                      ))}
                    </div>
                    <div style={styles.coefficientsSection}>
                      <strong>Products:</strong>
                      {equationResult.products.map((p, i) => (
                        <div key={i} style={styles.coefficientRow}>
                          {p.coefficient} {p.formula}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div style={styles.infoBox}>
              <strong>Note:</strong> Enter equations using formulas (e.g., H2O, CO2) or SMILES notation.
              Use &quot;→&quot; or &quot;=&quot; to separate reactants and products. Example: &quot;H2 + O2 → H2O&quot;
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  utilityTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    borderBottom: '1px solid #e2e8f0',
  },
  utilityTab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: '14px',
    fontWeight: 500,
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  utilityTabActive: {
    color: '#2563eb',
    borderBottomColor: '#2563eb',
  },
  utilityContent: {
    flex: 1,
  },
  section: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#475569',
  },
  inputGroup: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  input: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#1e293b',
  },
  calculateButton: {
    padding: '10px 20px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  errorBox: {
    padding: '12px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#991b1b',
    fontSize: '13px',
    marginBottom: '12px',
  },
  resultBox: {
    padding: '16px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
    marginBottom: '12px',
  },
  resultTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '8px',
  },
  resultValue: {
    fontSize: '14px',
    color: '#047857',
    marginBottom: '4px',
  },
  equationResult: {
    fontSize: '14px',
    color: '#047857',
  },
  balancedEquation: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    border: '1px solid #bbf7d0',
    textAlign: 'center',
  },
  coefficientsTable: {
    display: 'flex',
    gap: '20px',
    marginTop: '12px',
  },
  coefficientsSection: {
    flex: 1,
    fontSize: '13px',
    color: '#047857',
  },
  coefficientRow: {
    marginTop: '4px',
    padding: '4px 8px',
    backgroundColor: '#f0fdf4',
    borderRadius: '4px',
  },
  infoBox: {
    padding: '12px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fde68a',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#92400e',
    marginTop: '12px',
  },
};

